import React from 'react';
import { Plus } from 'lucide-react';
import QuantityCounter from './QuantityCounter';

/**
 * Club_ProductCard (Figma 3076:2597).
 *
 * El set tiene dos propiedades: `Producto` (Weed | Otros | Almacén) y `State`
 * (Default | Hover | Selected | filled). Aquí van las dos primeras familias, que
 * son las del POS; la de Almacén se añadirá al alinear esa pantalla.
 *
 *   Producto=Weed    peso y euro, dos campos vinculados por el precio
 *   Producto=Otros   contador de unidades
 *
 * El estado NO se pasa desde fuera: se deriva de si hay cantidad, que es como se
 * comporta el frame. Con cantidad, el borde pasa a --primary (Weed) o --ring
 * (Otros), aparece sombra y el botón se vuelve primario. Antes esto estaba
 * reimplementado en cada pantalla y cada copia decidía distinto.
 */
export default function ProductCard({
  variant = 'otros',          // 'weed' | 'otros'
  name,
  category,
  genetics = null,            // solo Weed: la píldora sobre el nombre
  price,
  unitLabel = 'u',
  stock = null,
  image = null,               // nodo ya resuelto (foto o placeholder de categoría)
  /** Weed: { peso, euro }. Otros: número de unidades. */
  value,
  onChange,
  onAdd,
  disabled = false,
  addLabel = 'Añadir',
  /** La rejilla del POS identifica el botón por producto en sus tests. */
  addTestId = 'add-btn',
  children = null,            // extras de la pantalla (p. ej. chips de ubicación)
}) {
  const esWeed = variant === 'weed';
  const conCantidad = esWeed
    ? Number(value?.peso) > 0 || Number(value?.euro) > 0
    : Number(value) > 0;

  /*
   * Los tres estados del frame (`Club_ProductCard`, Default / Hover / Selected)
   * se diferencian EN UNA SOLA COSA: el color del trazo. Ni sombra, ni relleno,
   * ni escala — el borde y nada más.
   *
   *   Default   `border`   #e5e5e5
   *   Hover     `ring`     #475569
   *   Selected  `ring`     #475569
   *
   * CUIDADO CON `ring`. En Figma esa variable vale #475569, sólido, el mismo
   * slate que `primary`. En `tokens.css`, `--ring` es OTRA cosa:
   * `rgba(71, 85, 105, 0.15)`, el halo del foco. Mismo nombre, dos valores.
   *
   * Usar `--ring` aquí deja el trazo a un 15%: presente en el DOM e invisible en
   * pantalla. El borde va en `--primary`, que es lo que la variable de Figma
   * resuelve de verdad.
   */
  const borde = disabled
    ? 'border-[var(--border)]'
    : conCantidad
      ? 'border-[var(--primary)]'
      : 'border-[var(--border)] hover:border-[var(--primary)]';

  return (
    <div
      data-testid="product-card"
      data-state={conCantidad ? (esWeed ? 'filled' : 'selected') : 'default'}
      className={`flex flex-col gap-3 rounded-lg border bg-[var(--card)] p-2 transition-colors ${borde} ${
        disabled ? 'opacity-60' : ''
      }`}
    >
      <div className="relative overflow-hidden rounded-md bg-[var(--muted)]">{image}</div>

      <div className="flex flex-col gap-2">
        {/* La categoría va DEBAJO de la imagen, no superpuesta: encima competía
            con la foto del producto y sobre las claras no había contraste que
            valiera. Es la Badge del frame (`Club_ProductCard` → Header
            Container), alineada a la izquierda y sobre el nombre. */}
        {category && (
          <span className="w-fit rounded-sm bg-[var(--muted)] px-2 py-0.5 text-xs font-medium text-[var(--muted-foreground)]">
            {category}
          </span>
        )}

        {genetics && (
          <span className="w-fit rounded-sm bg-[var(--muted)] px-2 py-0.5 text-xs font-medium text-[var(--secondary-foreground)]">
            {genetics}
          </span>
        )}

        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            {/* Dos líneas, no una: con el contador al lado, "APPLE FRITTER PURA" y
                "APPLE FRITTER CON TABACO" quedaban las dos en "APPLE FRITT…" y el
                camarero solo podía distinguirlas por el precio (reporte Kong House,
                20-ago-2026). El min-h reserva las dos líneas para que la rejilla no
                baile entre nombres cortos y largos. */}
            <p className="line-clamp-2 min-h-10 text-sm font-medium text-[var(--foreground)]" title={name}>{name}</p>
            <p className="text-xs text-[var(--muted-foreground)] tabular-nums">
              {price} €{esWeed ? `/${unitLabel}` : ''}
            </p>
          </div>
          {!esWeed && (
            <QuantityCounter
              value={value ?? ''}
              onChange={onChange}
              max={stock == null ? Infinity : stock}
              disabled={disabled}
              label={`Cantidad de ${name}`}
            />
          )}
        </div>

        {children}
      </div>

      <div className="mt-auto flex flex-col gap-3">
        {esWeed && (
          <div className="flex gap-2">
            {[
              ['Peso', 'peso', '0.0'],
              ['Euro', 'euro', '0.0'],
            ].map(([etiqueta, campo, ph]) => (
              <label key={campo} className="flex flex-1 flex-col gap-1">
                <span className="text-xs text-[var(--muted-foreground)]">{etiqueta}</span>
                <input
                  type="text"
                  inputMode="decimal"
                  disabled={disabled}
                  value={value?.[campo] ?? ''}
                  placeholder={ph}
                  onChange={(e) => onChange?.({ ...value, [campo]: e.target.value })}
                  className="h-8 w-full rounded-sm border border-[var(--border)] bg-[var(--background)] px-2 text-sm tabular-nums text-[var(--foreground)] placeholder:text-[var(--muted-foreground)] focus:outline-none focus:ring-1 focus:ring-[var(--foreground)] disabled:bg-[var(--muted)]"
                />
              </label>
            ))}
          </div>
        )}

        <button
          type="button"
          data-testid={addTestId}
          onClick={onAdd}
          disabled={disabled}
          // Primario SIEMPRE que se pueda pulsar, no solo con cantidad tecleada: en
          // blanco sobre tarjeta blanca el botón se perdía ("el Añadir en blanco es
          // algo liante", Kong House 20-ago). Es además cómo lo pinta ya la carta
          // del club social. El estado "sin stock" se queda gris y apagado — el
          // color no puede mentir.
          className={`inline-flex h-8 w-full items-center justify-center gap-1 rounded-sm text-sm font-medium transition-colors active:scale-[0.98] ${
            disabled
              ? 'cursor-not-allowed bg-[var(--muted)] text-[var(--muted-foreground)]'
              : 'bg-[var(--primary)] text-[var(--primary-foreground)] hover:bg-[var(--primary-strong)]'
          }`}
        >
          <Plus size={14} strokeWidth={1.5} />
          {disabled ? 'Sin stock' : addLabel}
        </button>
      </div>
    </div>
  );
}
